import { useQuery } from "@apollo/client";
import { Box, Button, Typography } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { ReactElement } from "react";

import {
  ListTollFreeRegistrationsQuery,
  TollFreeSubmissionStatus,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { ButtonLink } from "~frontend/components/Link";
import {
  DELETE_TOLL_FREE_REGISTRATION,
  LIST_TOLL_FREE_REGISTRATIONS,
} from "~frontend/graphql/tollFreeRegistrations";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

const RegistrationStatusBox = ({
  registrationId,
  registrationSubmittedToStaffAt,
  registrationCreatedAt,
  state,
  organizationName,
  registrationCreatedBy,
  registrationSubmittedToStaffBy,
  onDelete,
}: {
  registrationId: string;
  registrationSubmittedToStaffAt?: string;
  registrationCreatedAt?: string;
  state: "DRAFT" | "PENDING" | "PROVISIONING";
  organizationName?: string | null;
  registrationCreatedBy?: { fullName: string } | null;
  registrationSubmittedToStaffBy?: { fullName: string } | null;
  onDelete: () => void;
}): ReactElement => {
  const { subaccountLink } = useSubaccount()!;
  const confirm = useConfirm();
  const isStaff = useCurrentUserIsStaff();

  let stage;
  const buttonLabel =
    state === "DRAFT" ? "Review and Edit Draft" : "Review Submission";

  const handleDelete = async () => {
    const { confirmed } = await confirm({
      title: "Delete Toll-Free Registration",
      description: `Are you sure you want to delete the toll-free registration${organizationName ? ` for ${organizationName}` : ""}?`,
    });
    if (confirmed) {
      onDelete();
    }
  };

  if (state === "DRAFT") {
    stage = "Draft";
  } else if (state === "PENDING") {
    stage = "Under review";
  }

  return (
    <FlexBox
      sx={{
        p: 3,
        color: state === "DRAFT" ? palette.darkGray : palette.navy,
        borderRadius: 0.5,
        border: state === "DRAFT" ? "1px solid" : undefined,
        borderColor: palette.midGray,
        backgroundColor: state !== "DRAFT" ? palette.lightOrange : undefined,
      }}
      justifyContent="space-between"
      alignItems="flex-end"
    >
      <Box sx={{ alignSelf: "flex-start" }}>
        <FlexBox direction="column" gap={0.5}>
          <Typography variant="body2Bold" component="div">
            Toll-Free Submission
            {organizationName ? ` for ${organizationName}` : ""}: {stage}
          </Typography>
          {registrationSubmittedToStaffAt ? (
            <Typography variant="body2" component="div">
              <strong>Submitted:</strong>{" "}
              {new Date(registrationSubmittedToStaffAt).toLocaleDateString()}{" "}
              {registrationSubmittedToStaffBy &&
                `by ${registrationSubmittedToStaffBy.fullName}`}
            </Typography>
          ) : (
            <div />
          )}
          {registrationCreatedAt && (
            <Typography variant="body2" component="div">
              <strong>Created:</strong>{" "}
              {new Date(registrationCreatedAt).toLocaleDateString()}{" "}
              {registrationCreatedBy && `by ${registrationCreatedBy.fullName}`}
            </Typography>
          )}
          {registrationSubmittedToStaffAt && (
            <Box>
              <Typography variant="body2" component="div">
                Your toll-free registration is under review. The external vendor
                typically needs 3-5 business days to approve registrations.
              </Typography>
              <Typography variant="body2" component="div">
                Questions? Reach out to{" "}
                <a href="mailto:compliance@scaletowin.com">
                  compliance@scaletowin.com
                </a>
                .
              </Typography>
            </Box>
          )}
        </FlexBox>
      </Box>
      <FlexBox direction="column" alignItems="flex-end" gap={1}>
        <ButtonLink
          sx={{
            border: "1px solid",
            borderColor: state === "DRAFT" ? palette.darkGray : palette.navy,
          }}
          to={subaccountLink(
            `/phone-numbers/toll-free-registration/${registrationId}`
          )}
        >
          <Typography
            variant="label3Caps"
            component="div"
            sx={{ color: state === "DRAFT" ? palette.darkGray : palette.navy }}
          >
            {buttonLabel}
          </Typography>
        </ButtonLink>
        {isStaff && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleDelete}
          >
            <Typography variant="label3Caps" component="div">
              Delete
            </Typography>
          </Button>
        )}
      </FlexBox>
    </FlexBox>
  );
};

export const InProgressTollFreeRegistrations = (): ReactElement => {
  const { subaccount } = useSubaccount()!;
  const {
    data: tfRegistrations,
    loading,
    error,
    refetch,
  } = useQuery<ListTollFreeRegistrationsQuery>(LIST_TOLL_FREE_REGISTRATIONS, {
    variables: {
      input: {
        subaccountId: subaccount!.id,
      },
    },
    skip: !subaccount,
  });

  const [deleteTollFreeRegistration] = useMutation(
    DELETE_TOLL_FREE_REGISTRATION,
    {
      onErrorSnackbarMessage: "Failed to delete toll-free registration",
    }
  );

  const handleDelete = async (registrationId: string) => {
    const result = await deleteTollFreeRegistration({
      variables: {
        input: { registrationId },
      },
    });

    if (!result.errors) {
      void refetch();
    }
  };

  return (
    <AsyncWrapper data={tfRegistrations} loading={loading} errors={error}>
      {({ listTollFreeRegistrations }) => (
        <FlexBox direction="column" gap={1}>
          {listTollFreeRegistrations.map(registration => {
            const displayName =
              registration.displayName || registration.organizationName;

            const baseParams = {
              organizationName: displayName,
              registrationId: registration.id,
              registrationSubmittedToStaffAt: registration.submittedToStaffAt,
              registrationSubmittedToStaffBy: registration.submittedToStaffBy,
              registrationCreatedAt: registration.createdAt,
              registrationCreatedBy: registration.createdBy,
              onDelete: () => handleDelete(registration.id),
            };

            const key = `registration-status-${registration.id}`;

            if (
              registration.submissionStatus !==
              TollFreeSubmissionStatus.Verified
            ) {
              if (
                registration.submissionStatus === TollFreeSubmissionStatus.Draft
              ) {
                return (
                  <RegistrationStatusBox
                    {...baseParams}
                    state="DRAFT"
                    key={key}
                  />
                );
              } else {
                return (
                  <RegistrationStatusBox
                    {...baseParams}
                    state="PENDING"
                    key={key}
                  />
                );
              }
            }
          })}
        </FlexBox>
      )}
    </AsyncWrapper>
  );
};
